import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  GenerateShoppingListPdfResultSchema,
  ShoppingListSchema,
  type GenerateShoppingListInput,
  type GenerateShoppingListPdfResult,
  type ShoppingList,
} from "@/api/types/shoppingList";

export const shoppingListKeys = {
  all: ["shopping-list"] as const,
  detail: (eventId: string | undefined) => [...shoppingListKeys.all, eventId] as const,
};

function invalidate(queryClient: QueryClient, eventId: string) {
  void queryClient.invalidateQueries({ queryKey: shoppingListKeys.detail(eventId) });
}

/**
 * The generated shopping list for an event, if one exists yet. 404 before
 * the first "Generate shopping list" click is expected — the caller
 * (MenuShoppingListCard) treats a missing list as "not generated" rather
 * than an error state.
 */
export function useShoppingList(eventId: string | undefined) {
  return useQuery<ShoppingList | null>({
    queryKey: shoppingListKeys.detail(eventId),
    queryFn: async () => {
      try {
        const { data } = await apiClient.get(`/v2/events/${eventId}/shopping-list`);
        return validateResponse(
          ShoppingListSchema,
          data,
          "GET /v2/events/:id/shopping-list",
        );
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    enabled: !!eventId,
    staleTime: 15 * 1000,
  });
}

/**
 * Pure server math — guest_count x drinks_per_guest x menu share, rolled up
 * per ingredient with the buffer applied. The frontend never computes any
 * of these numbers; it only renders what comes back (see shoppingListFormat.ts
 * for the 1-decimal display rounding on oz, which is display-only).
 */
export function useGenerateShoppingList() {
  const queryClient = useQueryClient();
  return useMutation<ShoppingList, unknown, GenerateShoppingListInput>({
    mutationFn: async ({ eventId, ...body }) => {
      const { data } = await apiClient.post(
        `/v2/events/${eventId}/shopping-list/generate`,
        body,
      );
      return validateResponse(
        ShoppingListSchema,
        data,
        "POST /v2/events/:id/shopping-list/generate",
      );
    },
    onSuccess: (list, variables) => {
      queryClient.setQueryData(shoppingListKeys.detail(variables.eventId), list);
      invalidate(queryClient, variables.eventId);
    },
  });
}

/**
 * Generates the shopping-list PDF. Same "{pdf_key} or a URL" contract as
 * the menu PDF — see useGenerateMenuPdf's doc comment for the resolution.
 */
export function useGenerateShoppingListPdf() {
  return useMutation<GenerateShoppingListPdfResult, unknown, string>({
    mutationFn: async (eventId) => {
      const { data } = await apiClient.post(`/v2/events/${eventId}/shopping-list/pdf`, {});
      return validateResponse(
        GenerateShoppingListPdfResultSchema,
        data,
        "POST /v2/events/:id/shopping-list/pdf",
      );
    },
  });
}
